/**
 * TimerWidget — global live timer shown in the Workspace header.
 * Loads any running timer, ticks elapsed time, and stops → time entry.
 * Other components can trigger a refresh via the `workspaceTimerChanged` event.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Play, Square, Clock } from 'lucide-react';
import { wsApi } from './wsApi';

const fmtElapsed = (totalSec) => {
  const s = Math.max(0, Math.floor(totalSec));
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');
  return `${h}:${m}:${sec}`;
};

export default function TimerWidget() {
  const [timer, setTimer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [desc, setDesc] = useState('');
  const [projectId, setProjectId] = useState('');
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);
  const tickRef = useRef(null);
  // Baseline elapsed (from the server) + the local clock reading when we got
  // it. Elapsed is derived as base + (now - anchor), so we never parse the
  // server's naive datetime string (which JS would read in the wrong timezone).
  const anchorRef = useRef({ base: 0, at: Date.now() });

  const refresh = useCallback(() => {
    wsApi.getTimer().then(setTimer).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    wsApi.listProjects().then(setProjects).catch(() => {});
    const handler = () => refresh();
    window.addEventListener('workspaceTimerChanged', handler);
    return () => window.removeEventListener('workspaceTimerChanged', handler);
  }, [refresh]);

  // Re-anchor whenever the timer (re)loads, using the server-computed elapsed.
  useEffect(() => {
    if (timer) {
      anchorRef.current = { base: Number(timer.elapsed_seconds) || 0, at: Date.now() };
    }
  }, [timer]);

  // tick every second while running
  useEffect(() => {
    if (timer) {
      tickRef.current = setInterval(() => force((n) => n + 1), 1000);
      return () => clearInterval(tickRef.current);
    }
  }, [timer]);

  const start = async () => {
    if (!projectId) {
      toast.error('Select a project');
      return;
    }
    if (!desc.trim()) {
      toast.error('Enter a description');
      return;
    }
    try {
      const t = await wsApi.startTimer({ description: desc || null, project_id: projectId || null });
      setTimer(t);
      setDesc(''); setProjectId(''); setOpen(false);
    } catch {
      toast.error('Could not start timer');
    }
  };

  const stop = async () => {
    try {
      const res = await wsApi.stopTimer();
      setTimer(null);
      toast.success(`Logged ${Math.round((res.duration_minutes / 60) * 10) / 10}h`);
      window.dispatchEvent(new Event('workspaceDataChanged'));
    } catch {
      toast.error('Could not stop timer');
    }
  };

  if (timer) {
    const a = anchorRef.current;
    const elapsedSec = a.base + (Date.now() - a.at) / 1000;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff3ec', border: '1px solid #fdba74', borderRadius: 10, padding: '7px 12px' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f54b01', animation: 'wsPulse 1.2s infinite' }} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: '#9a3412' }}>{fmtElapsed(elapsedSec)}</span>
          <span style={{ fontSize: 10.5, color: '#c2410c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
            {timer.description || timer.task_title || timer.project_name || 'Tracking…'}
          </span>
        </div>
        <button onClick={stop} title="Stop timer" style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f54b01', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 11px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          <Square size={12} fill="#fff" /> Stop
        </button>
        <style>{`@keyframes wsPulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--em-bg-card)', border: '1px solid var(--em-border)', borderRadius: 10, padding: '8px 13px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
        <Clock size={15} color="#f54b01" /> Start timer
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 44, right: 0, zIndex: 50, background: 'var(--em-bg-card)', border: '1px solid var(--em-border)', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.12)', padding: 14, width: 280 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Start a timer</div>
          <input style={inp} placeholder="What are you working on?" value={desc} onChange={(e) => setDesc(e.target.value)} autoFocus required />
          <select style={{ ...inp, marginTop: 8 }} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Select a project</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={start} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', marginTop: 10, background: '#f54b01', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 0', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <Play size={14} fill="#fff" /> Start
          </button>
        </div>
      )}
    </div>
  );
}

const inp = { width: '100%', border: '1px solid var(--em-border)', borderRadius: 8, padding: '8px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
